// frontend/src/App.js

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Layout & Helper Components
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute'; // For routes that require login
import { useTheme } from './hooks/useTheme';

// Lazy Load Page Components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Accounts = lazy(() => import('./pages/Accounts'));
const SymbolDetail = lazy(() => import('./pages/SymbolDetail'));
const GainsPage = lazy(() => import('./pages/GainsPage'));

// Loading Fallback Component
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

/**
 * The root component of the application. It sets up the router and defines all application routes.
 */
function App() {
  // Apply the theme to the document
  useTheme();

  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          {/* The Navigation component will conditionally render based on auth state */}
          <Navigation />

          <main className="main-content">
            <div className="container-fluid">
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  {/* --- Public Routes --- */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />

                  {/* --- Protected Routes --- */}
                  {/* All routes inside ProtectedRoute require the user to be logged in. */}
                  <Route path="/" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/portfolio" element={
                    <ProtectedRoute>
                      <PortfolioPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/transactions" element={
                    <ProtectedRoute>
                      <Transactions />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics" element={
                    <ProtectedRoute>
                      <Analytics />
                    </ProtectedRoute>
                  } />
                  <Route path="/accounts" element={
                    <ProtectedRoute>
                      <Accounts />
                    </ProtectedRoute>
                  } />
                  <Route path="/symbol/:identifier" element={
                    <ProtectedRoute>
                      <SymbolDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/gains" element={
                    <ProtectedRoute>
                      <GainsPage />
                    </ProtectedRoute>
                  } />

                  {/* --- Fallback / Not Found Route --- */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </div>
          </main>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;